import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LandingPage } from './pages/LandingPage';
import { AuthPage, AuthUser } from './pages/AuthPage';
import { TrackerWorkspace } from './components/TrackerWorkspace';
import { ContactDetailsPage } from './pages/ContactDetailsPage';
import { Header } from './components/Header';
import { Footer } from './components/Footer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3005';
const TOKEN_KEY = 'closersignal_token';
const USER_KEY = 'closersignal_user';

function DashboardShell({
    user,
    token,
    onLogout
}: {
    user: AuthUser;
    token: string;
    onLogout: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className="min-h-screen bg-slate-950">
            <div className="border-b border-white/10 bg-white/[0.02] backdrop-blur-xl px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-emerald-500/20">
                        CS
                    </div>
                    <span className="text-sm text-white/60">
                        {t('dashboard.connectedAs')} <span className="font-semibold text-white">{user.name}</span>
                    </span>
                </div>
                <button
                    onClick={onLogout}
                    className="text-sm px-4 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                >
                    {t('nav.logout')}
                </button>
            </div>
            <TrackerWorkspace token={token} />
        </div>
    );
}

function AppRoutes() {
    const { t } = useTranslation();
    const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState<AuthUser | null>(() => {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) as AuthUser : null;
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        async function loadCurrentUser() {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${API_URL}/api/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Session expired');
                }
                const data = await response.json();
                setUser(data.user);
                localStorage.setItem(USER_KEY, JSON.stringify(data.user));
            } catch {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
                setToken(null);
                setUser(null);
            } finally {
                setLoading(false);
            }
        }

        loadCurrentUser();
    }, [token]);

    function handleAuthenticated(nextToken: string, nextUser: AuthUser) {
        localStorage.setItem(TOKEN_KEY, nextToken);
        localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
        setToken(nextToken);
        setUser(nextUser);
        navigate('/dashboard');
    }

    function handleLogout() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
        navigate('/auth');
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
                {t('auth.loading')}
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route
                        path="/auth"
                        element={token && user ? <Navigate to="/dashboard" replace /> : <AuthPage onAuthenticated={handleAuthenticated} />}
                    />
                    <Route
                        path="/dashboard"
                        element={token && user ? <DashboardShell user={user} token={token} onLogout={handleLogout} /> : <Navigate to="/auth" replace />}
                    />
                    <Route
                        path="/dashboard/contact/:contactId"
                        element={token && user ? <ContactDetailsPage token={token} /> : <Navigate to="/auth" replace />}
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AppRoutes />
        </BrowserRouter>
    );
}

export default App;
